import React from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { Checkbox } from 'formik-material-ui';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as StampCampaignService from '../../api/stampCampaignService';
import * as StampCardService from '../../api/stampCardService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 500,
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(2),
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  header: {
    marginTop: theme.spacing(2),
  },
  actionContainer: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  titleContainer: {
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rewardTemplateContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  expiryInput: {
    width: 40,
  },
  expiryDate: {
    marginLeft: theme.spacing(),
    width: 90,
  },
}));

export interface Values {
  stampCampaignIdList: string[];
}

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: () => void;
  memberId: string;
  activeStampCampaignList: StampCampaignService.StampCampaign[];
};

const useRewardTitleStyles = makeStyles((theme) => ({
  root: ({ isSelected }: { isSelected: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(),
    borderRadius: 5,
    borderWidth: isSelected ? 2 : 1,
    borderStyle: 'solid',
    borderColor: isSelected
      ? theme.palette.primary.main
      : theme.palette.grey[300],
    width: 300,
    height: 80,
    marginLeft: theme.spacing(2),
  }),
}));

export function StampCampaignTitle({
  stampCampaignId,
  stampCampaignIdList,
  title,
  handleClick,
}: {
  stampCampaignId: string;
  stampCampaignIdList: string[];
  title: string;
  handleClick: (updateList: string[]) => void;
}) {
  const isSelected = stampCampaignIdList.includes(stampCampaignId);
  const classes = useRewardTitleStyles({ isSelected });
  return (
    <div
      className={classes.root}
      onClick={() => {
        if (isSelected) {
          handleClick(
            stampCampaignIdList.filter((id) => id !== stampCampaignId)
          );
        } else {
          handleClick(stampCampaignIdList.concat([stampCampaignId]));
        }
      }}
    >
      <Typography variant={isSelected ? 'h6' : 'body2'}>{title}</Typography>
    </div>
  );
}

export default function AddCardDialog({
  open,
  onClose,
  onSuccess,
  memberId,
  activeStampCampaignList,
}: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();

  const initialValues: Values = {
    stampCampaignIdList: [],
  };

  const ValidationSchema = Yup.object().shape({
    stampCampaignIdList: Yup.array().min(
      1,
      'Please select a stamp card to add'
    ),
  });

  const handleSubmit = async (values: Values, _: FormikHelpers<Values>) => {
    if (company && memberId) {
      try {
        if (values.stampCampaignIdList.length > 0) {
          await StampCardService.issueCard({
            companyId: company.id,
            membershipID: memberId,
            stampCampaignIDs: values.stampCampaignIdList,
          });
          onSuccess();
        }
      } catch (e: any) {
        console.log('Error: ', e);
      }
    }
  };

  // TODO - Display a loader
  if (!company) {
    return null;
  }

  if (activeStampCampaignList.length === 0) {
    return null;
  }

  return (
    <FullScreenDialog open={open} keepMounted>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, setFieldValue, values: { stampCampaignIdList } }) => (
          <Form noValidate>
            <div className={classes.box}>
              <IconButton onClick={onClose}>
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
            <Grid container>
              <Grid item xs={12}>
                <div className={classes.content}>
                  <Container maxWidth="md">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <div className={classes.titleContainer}>
                          <Typography variant="h4">
                            Select Card to Add
                          </Typography>
                        </div>
                      </Grid>

                      <Grid item xs={12}>
                        {activeStampCampaignList.map((stampCampaign, index) => (
                          <div
                            className={classes.rewardTemplateContainer}
                            key={index}
                          >
                            <Field
                              id={stampCampaign.id}
                              component={Checkbox}
                              type="checkbox"
                              name="stampCampaignIdList"
                              value={stampCampaign.id}
                            />
                            <StampCampaignTitle
                              stampCampaignId={stampCampaign.id}
                              stampCampaignIdList={stampCampaignIdList}
                              title={stampCampaign.name}
                              handleClick={(newList: string[]) => {
                                setFieldValue('stampCampaignIdList', newList);
                              }}
                            />
                          </div>
                        ))}
                      </Grid>

                      <Grid item xs={12}>
                        <div className={classes.actionContainer}>
                          <Button onClick={() => onClose()}>Cancel</Button>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Add Card
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </FullScreenDialog>
  );
}
